import {MapsAPILoader} from '@agm/core';
import {DatePipe, PlatformLocation} from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import {FormControl, FormGroup, NgForm, NgModel} from '@angular/forms';
import {URLSearchParams} from '@angular/http';
import {MdDialog, MdDialogConfig, MdDialogRef} from '@angular/material';
import {ActivatedRoute, Router} from '@angular/router';
import {} from 'googlemaps';
import * as moment from 'moment';
import * as momentTimeZone from 'moment-timezone';
import {FileUploader} from 'ng2-file-upload';
import {Subject} from 'rxjs';
import {map, publish, refCount, switchMap} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {BookingService} from '../../api/booking.service';
import {GmapsApiService} from '../../api/gmaps-api.service';
import {UserService} from '../../api/user.service';
import {NotificationServiceBus} from '../../notification/notification.service';
import {GLOBAL, ModalOptions} from '../../shared/global';
import {BA, BOOKING_NATURE} from '../../shared/model/booking-nature.enum';
import {BOOKING_STATE} from '../../shared/model/booking-state.enum';
import {Booking} from '../../shared/model/booking.entity';
import {Contact, DEAFContact} from '../../shared/model/contact.entity';
import {UserFilter} from '../../shared/model/user-filter.interface';
import {
  Administrator,
  BookingOfficer,
  IndividualClient,
  Interpreter,
  OrganisationalRepresentative,
  UserFactory,
} from '../../shared/model/user.entity';
import {Address} from '../../shared/model/venue.entity';
import {PopupComponent} from '../../shared/popup/popup.component';
import {PreferedAllocationService} from '../../shared/prefered-allocation.service';
import {RolePermission} from '../../shared/role-permission/role-permission';
import {SpinnerService} from '../../spinner/spinner.service';
import {BookingHeaderService} from '../booking-header/booking-header.service';
import {Steps} from './steps';
import { getTermsConditionUrl } from '../../shared/terms-condition';

const _ONE_HOUR = 1000 /*milliseconds*/ * 60 /*seconds*/ * 60 /*minutes*/;

const MapAddressTypes = {
  floor: 'short_name',
  subpremise: 'short_name',
  street_number: 'short_name',
  route: 'long_name',
  locality: 'long_name',
  administrative_area_level_1: 'short_name',
  postal_code: 'short_name',
};

const MapAddressAttrs = {
  floor: 'unit_number',
  subpremise: 'unit_number',
  street_number: 'street_number',
  route: 'street_name',
  locality: 'suburb',
  administrative_area_level_1: 'state',
  postal_code: 'post_code',
};

const MapSubpremiseAddressAttrs = {
  floor: 'unit_number',
  premise: 'unit_number',
  subpremise: 'street_number',
  street_number: 'unit_number',
  route: 'street_name',
  locality: 'suburb',
  administrative_area_level_1: 'state',
  postal_code: 'post_code',
};

const InterpreterParentType = {
  VisualFrame: 'DeafBlind',
  Tactile: 'DeafBlind',
  Platform: 'DeafBlind',
  ASL: 'OtherLanguage',
  BSL: 'OtherLanguage',
  ISL: 'OtherLanguage',
  SignedEnglish: 'OtherLanguage',
  IndigenousSign: 'OtherLanguage',
};

@Component({
  selector: 'app-booking-form',
  templateUrl: './booking-form.component.html',
  styleUrls: ['./booking-form.component.sass'],
  encapsulation: ViewEncapsulation.None,
})
export class BookingFormComponent implements OnInit, OnDestroy, AfterViewChecked {
  steps = new Steps(7, 1);

  stepSet(step: number) {
    if (step <= this.steps.current || this.validateForm()) {
      for (let currentStep = this.steps.current; currentStep < step; currentStep++) {
        const currentForm: NgForm = this['bookingFormStep' + currentStep];
        if (currentForm.invalid) {
          return;
        }
      }
      this.steps.set(step);
    }
  }

  stepNext() {
    if (!this.validateForm()) {
      if (this.steps.current === 3 && !this.isVenueAddressValid()) {
        this.expandVenueAddress(true);
      }
      return;
    }
    this.steps.next();
  }

  validateForm(currentStep = this.steps.current, showError = true) {
    const currentForm: NgForm = this['bookingFormStep' + currentStep];
    if (currentForm.invalid) {
      validateAllFormFields(currentForm.control);
      if (showError) {
        this.notificationServiceBus.launchNotification(true, GLOBAL.MISSING_SECTION_FIELDS_ERROR_MESSAGE);
      }
      return;
    }
    return true;
  }

  validateAllForms() {
    for (let currentStep = 1; currentStep < this.steps.total; currentStep++) {
      if (!this.validateForm(currentStep, false)) {
        return;
      }
    }
    return true;
  }

  @ViewChild('bookingFormStep1') bookingFormStep1: NgForm;
  @ViewChild('bookingFormStep2') bookingFormStep2: NgForm;
  @ViewChild('bookingFormStep3') bookingFormStep3: NgForm;
  @ViewChild('bookingFormStep4') bookingFormStep4: NgForm;
  @ViewChild('bookingFormStep5') bookingFormStep5: NgForm;
  @ViewChild('bookingFormStep6') bookingFormStep6: NgForm;
  @ViewChild('bookingFormStep7') bookingFormStep7: NgForm;

  isBookingFormValid() {
    return (
      (this.bookingFormStep1.valid || this.bookingFormStep1.disabled) &&
      this.bookingFormStep2.valid &&
      this.bookingFormStep3.valid &&
      this.bookingFormStep4.valid &&
      (this.bookingFormStep5.valid || this.bookingFormStep5.disabled) &&
      this.bookingFormStep6.valid &&
      this.bookingFormStep7.valid
    );
  }

  InterpreterKindsFields = [
    {name: 'Auslan', label: 'Auslan Interpreters', value: 'number_of_auslan_interpreters_required', requireCount: 2},
    {name: 'Deaf', label: 'Deaf Interpreters', value: 'number_of_deaf_interpreters_required', requireCount: 2},
    {name: 'Captioning', label: 'Captioners', value: 'number_of_captioners_required'},
    {name: 'Notetaking', label: 'Notetakers', value: 'number_of_note_takers_required', requireCount: 2},
    {
      name: 'VisualFrame',
      label: 'Visual Frame Interpreters',
      value: 'number_of_visual_frame_interpreters_required',
      requireCount: 2,
    },
    {name: 'Tactile', label: 'Tactile Interpreters', value: 'number_of_tactile_interpreters_required', requireCount: 2},
    {
      name: 'Platform',
      label: 'Platform Interpreters',
      value: 'number_of_platform_interpreters_required',
      requireCount: 2,
    },
    {name: 'ASL', label: 'ASL Interpreters', value: 'number_of_asl_interpreters_required', requireCount: 2},
    {name: 'BSL', label: 'BSL Interpreters', value: 'number_of_bsl_interpreters_required', requireCount: 2},
    {name: 'ISL', label: 'ISL Interpreters', value: 'number_of_isl_interpreters_required', requireCount: 2},
    {
      name: 'SignedEnglish',
      label: 'Signed English Interpreters',
      value: 'number_of_signed_english_interpreters_required',
      requireCount: 2,
    },
    {
      name: 'IndigenousSign',
      label: 'Indigenous Sign Interpreters',
      value: 'number_of_indigenous_sign_interpreters_required',
      requireCount: 2,
    },
  ];

  @ViewChild('bookableModel') bookableModel: NgModel;

  bookingHeading = 'New Booking';

  bookingModel = new Booking();
  oldBookingModel;

  bookable: IndividualClient | OrganisationalRepresentative;

  bulkUploader: FileUploader = new FileUploader({url: '', maxFileSize: 20 * 1024 * 1024});

  requestedBy = undefined;

  interpreterKinds = {
    Auslan: true,
    Deaf: false,
    DeafBlind: false,
    Captioning: false,
    Notetaking: false,
    OtherLanguage: false,
    VisualFrame: false,
    Tactile: false,
    Platform: false,
    ASL: false,
    BSL: false,
    ISL: false,
    SignedEnglish: false,
    IndigenousSign: false,
  };

  bookingDate: Date;
  bookingStartTime: Date;
  bookingEndTime: Date;
  bookingDuration: string;
  isRecurringBooking: boolean;
  minDate: Date;
  maxDate: Date;
  minDateForRecurrenceEnd: Date;
  defaultDateTime: Date;
  repeat_days = [
    {
      display: 'S',
      value: 'Sunday',
      selected: false,
    },
    {
      display: 'M',
      value: 'Monday',
      selected: false,
    },
    {
      display: 'T',
      value: 'Tuesday',
      selected: false,
    },
    {
      display: 'W',
      value: 'Wednesday',
      selected: false,
    },
    {
      display: 'T',
      value: 'Thursday',
      selected: false,
    },
    {
      display: 'F',
      value: 'Friday',
      selected: false,
    },
    {
      display: 'S',
      value: 'Saturday',
      selected: false,
    },
  ];

  @ViewChild('searchAddress') public searchAddressElementRef: ElementRef;
  venueAddressExpand = false;
  travelCostApplicable;

  techAudioSources = [
    'Smartphone / Tablet',
    'Desktop Computer',
    'Laptop',
    'AV System Patch-In',
    'Telephone Link',
    'Other (specify in notes)',
  ];
  techPlatforms = [
    'Skype',
    'Google Hangouts',
    'Zoom',
    'Adobe Connect',
    'Echo360',
    'Livestream',
    'Webex',
    'Gotomeeting',
    'Gotowebinar',
    'Youtube Live',
    'Other (specify in notes)',
  ];
  techHardware = ['Computer', 'Laptop', 'iPad', 'Smartphone', 'VC system', 'Other (specify in notes)'];

  primaryContact = undefined;
  currentUserIsContact = true;

  appointmentTypes = Object.keys(BOOKING_NATURE)
    .filter((value) => {
      return (
        (value === BOOKING_NATURE[value] || BOOKING_NATURE[value].startsWith(value)) &&
        value !== BOOKING_NATURE[BOOKING_NATURE.None]
      );
    })
    .map((v) => BOOKING_NATURE[v]) as string[];
  specificAppointmentTypes = [];

  isStandardInstruction = false;

  uploader: FileUploader = new FileUploader({url: '', maxFileSize: 20 * 1024 * 1024});
  oldDocuments = [];
  deleteDocuments = [];

  deafClientContact = undefined;
  deafClientContactExtra = undefined;
  currentUserIsClient = true;

  oldInterpreterPreference = [];
  hasPreferred = false;
  showPreferred = false;
  showProfilePreferred = false;
  hasBlocked = false;
  showBlocked = false;
  showProfileBlocked = false;
  preferAllocSub: any;

  StatesNames = ['ACT', 'NSW', 'QLD', 'SA', 'TAS', 'VIC', 'WA', 'NT'];
  billingContact = undefined;
  billingDetail = undefined;

  termsAndConditionAccepted = false;

  allClientsOrgSubject = new Subject<string>();
  allClientsOrg$ = this.allClientsOrgSubject.pipe(
    switchMap((query) => {
      const userType = camelCaseToUnderScore(this.bookingModel.bookable_type) + 's';
      return this.userService
        .fetchUsersOfType(userType, {
          pagination: false,
          search_text: query,
        })
        .pipe(
          map((res: any) => {
            return res.data.users.map((user) => {
              const singleUser = <IndividualClient | OrganisationalRepresentative>UserFactory.createUser(user);
              singleUser.displayName = getOrgName(singleUser);
              return singleUser;
            });
          })
        );
    }),
    publish(),
    refCount()
  );

  dialogRef: MdDialogRef<any>;
  dialogSub;

  routeQueryParamSub;
  headerSubscription;
  forEdit: boolean;
  isDuplicate: boolean;
  duplicatingBookable: number;
  assignedInterpreter = 0;
  isDisableForClientOrgBookOfficer = false;
  isDisabledForLinkedClient: boolean;
  filterUserParams = new URLSearchParams();
  userFilter: UserFilter = {};
  clientAvailability = false;
  isClientFound = false;
  isClientAvailable = null;
  hasClientId = false;
  clientId: any;
  previousClientId: any;
  availabilityMsg = '';
  standardInvoice = true;

  get isDisabledForAdmin() {
    return this.forEdit && !this.bookingModel.created_by_admin;
  }

  get isDisabledForOrgRepIndClient() {
    return this.isUserOrgRepORIndClientTemp() && this.forEdit;
  }

  get isUserAdminORBookOfficer() {
    return GLOBAL.currentUser instanceof Administrator || GLOBAL.currentUser instanceof BookingOfficer;
  }

  get userModel() {
    return this.isUserAdminORBookOfficer ? this.bookable : GLOBAL.currentUser;
  }

  isUserOrgRepORIndClientTemp() {
    return this.isUserOrgRep() || this.isIndClient();
  }

  isUserOrgRep() {
    return GLOBAL.currentUser instanceof OrganisationalRepresentative;
  }

  isIndClient() {
    return GLOBAL.currentUser instanceof IndividualClient;
  }

  isCurrentUserContact() {
    if (this.forEdit || this.isDuplicate) {
      return (
        this.bookingModel.client.email === this.bookingModel.primaryContact.email ||
        GLOBAL.currentUser.email === this.bookingModel.primaryContact.email
      );
    } else {
      return !this.isUserOrgRep();
    }
  }

  isCurrentUserClient() {
    if (this.forEdit || this.isDuplicate) {
      return (
        this.bookingModel.deaf_person.email === this.bookingModel.primaryContact.email ||
        GLOBAL.currentUser.email === this.bookingModel.primaryContact.email
      );
    } else {
      return !this.isUserOrgRep();
    }
  }

  constructor(
    public bookingService: BookingService,
    private userService: UserService,
    private platformLocation: PlatformLocation,
    private datePipe: DatePipe,
    private notificationServiceBus: NotificationServiceBus,
    private spinnerService: SpinnerService,
    private rolePermission: RolePermission,
    private router: Router,
    private route: ActivatedRoute,
    public bookingHeaderService: BookingHeaderService,
    private viewContainerRef: ViewContainerRef,
    private dialog: MdDialog,
    private mapsAPILoader: MapsAPILoader,
    private gmapApi: GmapsApiService,
    private ngZone: NgZone,
    private changeDetector: ChangeDetectorRef,
    private _sharedPreferredAllocationService: PreferedAllocationService,
    private cdRef: ChangeDetectorRef,
  ) {
    BA.loadItems();
    this.dateRestrictions();
  }

  onBeforeUnload = (e) => {
    if (environment.localhost) {
      return;
    }

    e = e || window.event;
    // check the changes only for first and second step to ensure form has some changes
    const hasChanges = this.bookingFormStep1.valid || this.bookingFormStep1.disabled || this.bookingFormStep2.valid;
    if (hasChanges) {
      e.preventDefault();
      return (e.returnValue = 'Are you sure to reload this page?');
    }
  }

  ngOnInit() {
    window.addEventListener('beforeunload', this.onBeforeUnload);
    this.routeQueryParamSub = this.route.queryParams.subscribe((params) => {
      const param = params['bookingModel'] || '';
      const shouldEdit = params['shouldEdit'] || '';
      const autoInviteInter = params['isAutoInviting'] || '';
      this.forEdit = Boolean(shouldEdit.length > 0 && shouldEdit === 'edit');
      this.assignedInterpreter = params['assignedInterpreter'] || '';
      this.isDuplicate = param.length > 0 && shouldEdit === '';

      if (param.length > 0) {
        let jsonData = JSON.parse(decodeURI(atob(param)));
        this.bookingModel.fromJSON(jsonData);
        this.bookingModel.auto_invitation = autoInviteInter.toLowerCase() === 'true';
        this.duplicatingBookable = jsonData.bookable_id;
        if (this.bookingModel.bookable_type === 'OrganisationalRepresentative') {
          this.currentUserIsClient = false;
          this.currentUserIsContact = false;
          if (!this.forEdit && !this.isDuplicate) {
            this.bookingModel.primaryContact = new Contact();
            this.bookingModel.deaf_person = new DEAFContact();
          }
        }
        this.oldDocuments = jsonData.documents_attributes;
        this.oldInterpreterPreference = jsonData.preference_allocations_attributes;
        const zonedDate = this.utcToBookingTimeZoneDate(this.bookingModel.venue.start_time_iso);
        this.bookingDate = new Date(zonedDate);
        this.setTime();
        this.natureOfAppointmentChange();
        this.checkInterpreterBoxes();
        this.expandVenueAddress(true);
      } else {
        this.newBooking();
      }

      if (this.forEdit) {
        this.bookingHeading = 'EDIT BOOKING';
        this.termsAndConditionAccepted = true;
        this.checkInterpreterBoxes();
        this.isDisableForClientOrgBookOfficer =
          (GLOBAL.currentUser instanceof BookingOfficer || this.isUserOrgRepORIndClientTemp()) &&
          this.isStateCancelClaimExportedUnableService(this.bookingModel.state);
        this.expandVenueAddress(true);
      } else {
        this.bookingHeading = 'NEW BOOKING';
        this.bookingModel.bookable_type = this.bookingModel.bookable_type || 'IndividualClient';
        this.bookingModel.bookable_id = 0;
        this.roundOffMinutes();
      }
    });

    this.attachAddressAutocomplete();

    if (GLOBAL.currentUser !== undefined) {
      this.setIsStandardInstruction();

      if (this.bookingModel.bookable_type === 'IndividualClient') {
        this.currentUserIsContact = this.isCurrentUserContact();
        this.currentUserIsClient = this.isCurrentUserClient();
      }

      if (this.forEdit) {
        this.getUserById(this.bookingModel.bookable_id);
      }

      if (this.isDuplicate) {
        this.getUserById(this.duplicatingBookable);
        this.standardInvoice = this.isStandardInvoice();
      }
      if (!this.forEdit && !this.isDuplicate) {
        if (this.isUserOrgRepORIndClientTemp()) {
          this.bookable = GLOBAL.currentUser;
          this.bookable.displayName = getOrgName(this.bookable);
          this.bookingModel.bookable_type = this.bookable.user_type;
        }

        this.onPrimaryContactChange();
        this.setClientAsRequestedBy();
        this.onClientSelectionChange();
        this.onStandardInvoice();
      }
      this.setUser();
      this.bookingModel.bookable_type = this.bookingModel.bookable_type || 'IndividualClient';
      if (this.isUserAdminORBookOfficer) {
        this.bookingModel.created_by_admin = true;
      }
      this.oldBookingModel = this.deepCopy(this.bookingModel);

      if (!this.forEdit && !this.isDuplicate) {
        this.setStandardVenueAddress();
      }
      if (!this.forEdit && (this.isIndClient() || this.isUserOrgRep())) {
        this.setUserCertifications(GLOBAL.currentUser);
      }
      this.travelCostApplicable = this.bookingModel.travel_cost_applicable;
      this.headerSubscription = this.bookingHeaderService.notifyObservable$.subscribe((res) => {
        this.callRelatedFunctions(res);
      });
      this.isDisabledForLinkedClient =
        this.bookingModel.bookable_type === 'OrganisationalRepresentative' &&
        GLOBAL.currentUser instanceof IndividualClient;
      if (this.bookingModel.linkedClientId > 0 && this.bookingModel.bookable_type === 'OrganisationalRepresentative') {
        this.availabilityMsg =
          this.bookingModel.deaf_person.first_name +
          ' ' +
          this.bookingModel.deaf_person.last_name +
          ' is connected to this booking';
        this.isClientFound = true;
        this.clientAvailability = true;
        this.isClientAvailable = true;
        this.hasClientId = true;
        this.previousClientId = this.bookingModel.linkedClientId;
      }
    }
    this.dateRestrictions();
  }

  ngAfterViewChecked() {
    if (this.bookable && (this.bookingFormStep1.controls['bookable'] && !this.bookingFormStep1.controls['bookable'].touched)) {
      this.bookingFormStep1.controls['bookable'].markAsTouched();
      this.cdRef.detectChanges();
    }
  }

  ngOnDestroy() {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    if (this.dialogSub != null) {
      this.dialogSub.unsubscribe();
    }
    GLOBAL._filterUserVal = new URLSearchParams();
    if (this.headerSubscription != null) {
      this.headerSubscription.unsubscribe();
    }
    if (this.preferAllocSub != null) {
      this.preferAllocSub.unsubscribe();
    }
    if (this.routeQueryParamSub != null) {
      this.routeQueryParamSub.unsubscribe();
    }
  }

  callRelatedFunctions(res) {
    if (res.hasOwnProperty('option')) {
      switch (res.option) {
        case 'refreshBooking':
          this.newBooking();
          this.bookable = undefined;
          break;
      }
    }
  }

  newBooking() {
    this.bookingModel = new Booking();
    this.bookingDate = undefined;
    this.bookingStartTime = undefined;
    this.bookingEndTime = undefined;
    this.isRecurringBooking = false;
    this.resetPrefBlockInterpreters();
    this.onSpecialInstruction();
    this.resetRecurringDays();
  }

  isStateCancelClaimExportedUnableService(state) {
    return (
      state === BOOKING_STATE.Claimed ||
      state === BOOKING_STATE.Cancelled_claimed ||
      state === BOOKING_STATE.Claimed_exported ||
      state === BOOKING_STATE.Cancelled_claimed_exported ||
      state === BOOKING_STATE.Cancelled_no_charge ||
      state === BOOKING_STATE.Unable_to_service
    );
  }

  setUser() {
    if (this.bookingModel.preference_allocations_attributes.some((item) => item.preference === 'preferred')) {
      this.showPreferred = true;
      this.showProfilePreferred = true;
    }

    if (this.bookingModel.preference_allocations_attributes.some((item) => item.preference === 'blocked')) {
      this.showBlocked = true;
      this.showProfileBlocked = true;
    }

    if (!this.showBlocked && !this.showPreferred) {
      this.bookingModel.preference_allocations_attributes = [];
    }
    if (this.isDuplicate) {
      this.bookingModel.preference_allocations_attributes.map((item) => delete item['id']);
    }
    this.preferAllocSub = this._sharedPreferredAllocationService.interpreterStream$.subscribe((data) => {
      this.filterUserPreference(data);
    });
  }

  deepCopy(oldObj: any) {
    if (this.forEdit) {
      let newObj = JSON.parse(JSON.stringify(oldObj));
      return newObj;
    }
  }

  isNewBooking() {
    return this.router.url.includes('create-booking');
  }

  resetPrefBlockInterpreters() {
    this.oldInterpreterPreference = [];
    this.showPreferred = this.showProfilePreferred = this.showBlocked = this.showProfileBlocked = false;
    this.hasPreferred = this.hasBlocked = false;
  }

  getUserById(id: number) {
    let bookableUser;
    this.userService.getUser(id).subscribe((res: any) => {
      if (res.status === 200) {
        bookableUser = res.data;
        const singleUser = <IndividualClient | OrganisationalRepresentative>UserFactory.createUser(bookableUser);
        singleUser.displayName = getOrgName(singleUser);
        this.bookable = singleUser;
        this.bookingModel.bookable_id = singleUser.id;
        this.setIsStandardInstruction();
      }
    });
  }

  onCreateBooking() {
    if (this.bookingModel.bookable_type === 'OrganisationalRepresentative' && this.isIndClient()) {
      return;
    }
    this.bookingModel.recurrence_end_date = this.datePipe.transform(
      this.bookingModel.recurrence_end_date,
      'yyyy-MM-dd'
    );
    this.bookingModel.travel_cost_applicable = !!this.travelCostApplicable;

    if (this.shouldSelectDeafBlindOtherLanguage()) {
      const msg = this.interpreterKinds.DeafBlind ? 'deaf blind interpreter' : '"Other Language Needs"';
      this.notificationServiceBus.launchNotification(true, 'Select at least one type of ' + msg);
      return;
    }

    if (!this.isBookingFormValid()) {
      this.validateAllForms();
      this.notificationServiceBus.launchNotification(true, GLOBAL.MISSING_FIELDS_ERROR_MESSAGE);
      return;
    }
    if (!this.termsAndConditionAccepted && !this.forEdit) {
      this.notificationServiceBus.launchNotification(true, 'Kindly accept Terms and Conditions');
      return;
    }
    if (this.bookingEndTime.getTime() < this.bookingStartTime.getTime()) {
      this.notificationServiceBus.launchNotification(
        true,
        'Sorry. The field(s) underneath are filled in incorrectly. END TIME'
      );
      return;
    }
    if (!this.isRecurringBooking) {
      this.bookingModel.frequency = null;
    }
    if (this.isInterpreterLessThanTwo() && this.isMoreInterpreterNeeded()) {
      let message =
        `This booking might require more than 1 professional. You've only requested 1.
                            Are you sure you want to ` +
        (this.forEdit ? 'update' : 'create') +
        ` this booking?`;
      let title = 'Warning: this booking might require more professionals';
      this.createModal(title, message);
      this.dialogSub = this.dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.proceedWithBooking();
        }
      });
    } else if (this.isLongBooking()) {
      let message = `This booking will take 12 hours or more. Are you sure you want to submit this booking?`;
      let title = 'Long Booking WARNING';
      this.createModal(title, message);
      this.dialogSub = this.dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.proceedWithBooking();
        }
      });
    } else {
      this.proceedWithBooking();
    }
  }

  validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({onlySelf: true});
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  // have to merge this with save booking later.
  proceedWithBooking() {
    if (this.isBookingTimeInNonStandardHours()) {
      let message =
        'This booking is not within the standard booking hours (8AM - 6PM).' +
        ' Do you still want to ' +
        (this.forEdit ? 'update' : 'create') +
        ' booking?';
      let title = 'NON-STANDARD HOURS WARNING';
      this.createModal(title, message);
      this.dialogSub = this.dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          if (this.forEdit) {
            this.updateLinkedBookings();
          } else {
            this.createBooking();
          }
        }
      });
    } else {
      if (this.forEdit) {
        this.updateLinkedBookings();
      } else {
        this.createBooking();
      }
    }
  }

  isBookingTimeInNonStandardHours() {
    let startDate = new Date(this.bookingStartTime);
    let endDate = new Date(this.bookingEndTime);

    return (
      startDate.getHours() < 6 ||
      ((endDate.getHours() === 20 && (endDate.getMinutes() > 0 || endDate.getSeconds() > 0)) || endDate.getHours() > 20)
    );
  }

  createBooking() {
    this.timeFormatting();
    if (!this.bookingModel.bookable_id) {
      this.bookingModel.bookable_id = GLOBAL.currentUser.id;
      this.bookingModel.bookable_type = GLOBAL.currentUser.type;
    }
    this.spinnerService.requestInProcess(true);
    this.bookingModel.state = BOOKING_STATE.Requested; // res.data.state;
    this.bookingModel.new_link_id_required = this.isRecurringBooking;
    this.bookingModel.recurring = this.isRecurringBooking;
    if (this.isRecurringBooking) {
      this.bookingModel.repeat_booking_on_days = this.getRecurrenceDays();
    }
    this.bookingService.createBooking(this.bookingModel).subscribe(
      (res: any) => {
        if (res.status === 204) {
          this.notificationServiceBus.launchNotification(false, 'The Booking has been created.');
          let route = this.rolePermission.getDefaultRouteForCurrentUser();
          this.router.navigate([route]);
        }
        this.spinnerService.requestInProcess(false);
      },
      (errors) => {
        this.spinnerService.requestInProcess(false);
        let e = errors.json() || '';
        this.notificationServiceBus.launchNotification(true, e);
      }
    );
  }

  updateLinkedBookings() {
    if (this.isUserAdminORBookOfficer && !!this.bookingModel.link_id) {
      const message = 'Would you like to save these changes for all bookings or only for this one?';
      const title = 'Change all bookings?';
      this.createModal(title, message, {
        okTitle: 'Update this booking',
        cancelTitle: 'Update all bookings',
        closeVal: 'cancel',
      });
      this.dialogSub = this.dialogRef.afterClosed().subscribe((updateOnlyThisBooking) => {
        if (updateOnlyThisBooking !== 'cancel') {
          this.bookingModel.update_all_linked_bookings = !updateOnlyThisBooking;
          this.updateBooking();
        }
      });
    } else {
      this.updateBooking();
    }
  }

  updateBooking() {
    if (
      (this.bookingModel.state === BOOKING_STATE.In_progress || this.bookingModel.state === BOOKING_STATE.Allocated) &&
      (this.assignedInterpreter > 0 && this.isImportantFieldsChanged())
    ) {
      let config: MdDialogConfig = {
        disableClose: true,
      };
      config.viewContainerRef = this.viewContainerRef;
      this.dialogRef = this.dialog.open(PopupComponent, config);
      this.dialogRef.componentInstance.title = 'Important Fields Changed';
      this.dialogRef.componentInstance.cancelTitle = 'Back';
      this.dialogRef.componentInstance.okTitle = 'Yes';
      this.dialogRef.componentInstance.popupMessage = `Interpreter(s) have been/is allocated for this job. You're changing important fields of the booking.
                 Do you have confirmation from the interpreter(s) that these changes are OK?`;

      this.dialogSub = this.dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.saveBooking();
        }
      });
    } else {
      this.saveBooking();
    }
  }

  saveBooking() {
    let result = this.assignInterpGreaterThanRequestedCategory();
    if (Boolean(result) && result.length > 0) {
      this.notificationServiceBus.launchNotification(
        true,
        'Oops! Too many ' + result + ' already allocated. Please unassign first.'
      );
      return;
    } else {
      this.spinnerService.requestInProcess(true);
      const bookingID = this.bookingModel.id;

      this.deleteDocuments.forEach((element) => {
        this.bookingModel.documents_attributes.push(element);
      });
      this.timeFormatting();
      this.bookingService.updateBooking(bookingID, this.bookingModel).subscribe(
        (res: any) => {
          if (res.status === 204 && res.ok === true) {
            this.notificationServiceBus.launchNotification(false, 'The Booking has been Updated.');
            this.gotoBookingInfo();
          }
          this.spinnerService.requestInProcess(false);
        },
        (errors) => {
          this.spinnerService.requestInProcess(false);
          let e = errors.json() || '';
          this.notificationServiceBus.launchNotification(true, e);
        }
      );
    }
  }

  isImportantFieldsChanged() {
    let selectedDate = this.datePipe.transform(this.bookingDate, 'yyyy-MM-dd');
    let startTime = moment(this.bookingStartTime, 'hh:mm A').format('HH:mm:ss');
    let endTime = moment(this.bookingEndTime, 'hh:mm A').format('HH:mm:ss');

    return (
      selectedDate !== this.datePipe.transform(this.oldBookingModel.start_time, 'yyyy-MM-dd') ||
      startTime !== moment(this.oldBookingModel.start_time, 'hh:mm A').format('HH:mm:ss') ||
      endTime !== moment(this.oldBookingModel.end_time, 'hh:mm A').format('HH:mm:ss') ||
      this.bookingModel.raw_nature_of_appointment !== this.oldBookingModel.nature_of_appointment ||
      this.bookingModel.specific_nature_of_appointment !== this.oldBookingModel.specific_nature_of_appointment ||
      this.bookingModel.venue.street_name !== this.oldBookingModel.address_attributes.street_name ||
      this.bookingModel.venue.state !== this.oldBookingModel.address_attributes.state ||
      this.bookingModel.venue.suburb !== this.oldBookingModel.address_attributes.suburb ||
      this.bookingModel.venue.post_code !== this.oldBookingModel.address_attributes.post_code
    );
  }

  assignInterpGreaterThanRequestedCategory() {
    return this.interpreterKinds.Captioning &&
      this.assignedInterpreter > this.bookingModel.number_of_captioners_required
      ? 'captioners'
      : this.interpreterKinds.Notetaking && this.assignedInterpreter > this.bookingModel.number_of_note_takers_required
      ? 'notetakers'
      : this.interpreterKinds.Auslan &&
        this.assignedInterpreter > this.bookingModel.number_of_auslan_interpreters_required
      ? 'auslan interpreters'
      : this.interpreterKinds.Deaf && this.assignedInterpreter > this.bookingModel.number_of_deaf_interpreters_required
      ? 'deaf interpreters'
      : this.interpreterKinds.VisualFrame &&
        this.assignedInterpreter > this.bookingModel.number_of_visual_frame_interpreters_required
      ? 'visual frame interpreters'
      : this.interpreterKinds.Tactile &&
        this.assignedInterpreter > this.bookingModel.number_of_tactile_interpreters_required
      ? 'tactile interpreters'
      : this.interpreterKinds.Platform &&
        this.assignedInterpreter > this.bookingModel.number_of_platform_interpreters_required
      ? 'platform interpreters'
      : this.interpreterKinds.ASL && this.assignedInterpreter > this.bookingModel.number_of_asl_interpreters_required
      ? 'ASL interpreters'
      : this.interpreterKinds.BSL && this.assignedInterpreter > this.bookingModel.number_of_bsl_interpreters_required
      ? 'BSL interpreters'
      : this.interpreterKinds.ISL && this.assignedInterpreter > this.bookingModel.number_of_isl_interpreters_required
      ? 'ISL interpreters'
      : this.interpreterKinds.SignedEnglish &&
        this.assignedInterpreter > this.bookingModel.number_of_signed_english_interpreters_required
      ? 'signed english interpreters'
      : this.interpreterKinds.IndigenousSign &&
        this.assignedInterpreter > this.bookingModel.number_of_indigenous_sign_interpreters_required
      ? 'signed indigenous interpreters'
      : '';
  }

  gotoBookingInfo() {
    let route =
      GLOBAL.currentUser instanceof Interpreter || GLOBAL.currentUser instanceof OrganisationalRepresentative
        ? 'job-detail'
        : 'booking-job';
    GLOBAL.selBookingID =
      Boolean(GLOBAL.selBookingID) && GLOBAL.selBookingID.length > 0 ? GLOBAL.selBookingID : this.bookingModel.id;
    this.router.navigate(['booking-management/' + GLOBAL.selBookingID, route]);
  }

  fireNotification(event, isLinkingClient?: boolean) {
    if ((event.target as Element).hasAttribute('readonly')) {
      if (isLinkingClient) {
        this.notificationServiceBus.launchNotification(
          true,
          'An individual client has been linked to this booking.' +
            ' You must unlink the client from the booking before changing this field.'
        );
      } else {
        this.notificationServiceBus.launchNotification(
          true,
          'In order to change this field, please contact the booking office.'
        );
      }
    }
  }

  isAutoInviting() {
    if (this.bookingModel.auto_invitation && this.checkUserAdminORBookOfficer()) {
      this.notificationServiceBus.launchNotification(
        true,
        'Currently auto inviting, please cancel auto inviting before updating.'
      );
    }
  }

  checkUserAdminORBookOfficer() {
    return GLOBAL.currentUser instanceof Administrator || GLOBAL.currentUser instanceof BookingOfficer;
  }

  serviceTypeChange(serviceType: string, cbName) {
    if (this.forEdit) {
      if (this.assignedInterpreter > 0) {
        this.interpreterKinds[serviceType] = cbName.checked = false;
        this.notificationServiceBus.launchNotification(
          true,
          'Oops. Deallocate interpreters before changing the service type.'
        );
        return;
      }

      Object.keys(this.interpreterKinds).forEach((type) => {
        if (serviceType !== type && InterpreterParentType[serviceType] !== type) {
          this.interpreterKinds[type] = false;
        }
      });

      [
        'number_of_auslan_interpreters_required',
        'number_of_deaf_interpreters_required',
        'number_of_deaf_blind_interpreters_required',
        'number_of_captioners_required',
        'number_of_note_takers_required',
        'number_of_visual_frame_interpreters_required',
        'number_of_tactile_interpreters_required',
        'number_of_platform_interpreters_required',
        'number_of_asl_interpreters_required',
        'number_of_bsl_interpreters_required',
        'number_of_isl_interpreters_required',
        'number_of_signed_english_interpreters_required',
        'number_of_indigenous_sign_interpreters_required',
      ].forEach((modelVal) => {
        this.bookingModel[modelVal] = 0;
      });
      this.changeDetector.detectChanges();
    } else {
      if (serviceType === 'Auslan') {
        this.bookingModel.number_of_auslan_interpreters_required = 0;
      } else if (serviceType === 'Deaf') {
        this.bookingModel.number_of_deaf_interpreters_required = 0;
      } else if (serviceType === 'DeafBlind') {
        this.interpreterKinds.VisualFrame = this.interpreterKinds.Tactile = this.interpreterKinds.Platform = false;
      } else if (serviceType === 'Captioning') {
        this.bookingModel.number_of_captioners_required = 0;
      } else if (serviceType === 'Notetaking') {
        this.bookingModel.number_of_note_takers_required = 0;
      } else if (serviceType === 'VisualFrame') {
        this.bookingModel.number_of_visual_frame_interpreters_required = 0;
      } else if (serviceType === 'Tactile') {
        this.bookingModel.number_of_tactile_interpreters_required = 0;
      } else if (serviceType === 'Platform') {
        this.bookingModel.number_of_platform_interpreters_required = 0;
      } else if (serviceType === 'OtherLanguage') {
        this.interpreterKinds.ASL =
          this.interpreterKinds.BSL = this.interpreterKinds.ISL = this.interpreterKinds.SignedEnglish = this.interpreterKinds.IndigenousSign = false;
      } else if (serviceType === 'ASL') {
        this.bookingModel.number_of_asl_interpreters_required = 0;
      } else if (serviceType === 'BSL') {
        this.bookingModel.number_of_bsl_interpreters_required = 0;
      } else if (serviceType === 'ISL') {
        this.bookingModel.number_of_isl_interpreters_required = 0;
      } else if (serviceType === 'SignedEnglish') {
        this.bookingModel.number_of_signed_english_interpreters_required = 0;
      } else if (serviceType === 'IndigenousSign') {
        this.bookingModel.number_of_indigenous_sign_interpreters_required = 0;
      }
    }
  }

  checkInterpreterBoxes() {
    this.interpreterKinds.Auslan = this.bookingModel.number_of_auslan_interpreters_required > 0;
    this.interpreterKinds.Deaf = this.bookingModel.number_of_deaf_interpreters_required > 0;
    this.interpreterKinds.Captioning = this.bookingModel.number_of_captioners_required > 0;
    this.interpreterKinds.Notetaking = this.bookingModel.number_of_note_takers_required > 0;
    this.interpreterKinds.VisualFrame = this.bookingModel.number_of_visual_frame_interpreters_required > 0;
    this.interpreterKinds.Tactile = this.bookingModel.number_of_tactile_interpreters_required > 0;
    this.interpreterKinds.Platform = this.bookingModel.number_of_platform_interpreters_required > 0;
    this.interpreterKinds.ASL = this.bookingModel.number_of_asl_interpreters_required > 0;
    this.interpreterKinds.BSL = this.bookingModel.number_of_bsl_interpreters_required > 0;
    this.interpreterKinds.ISL = this.bookingModel.number_of_isl_interpreters_required > 0;
    this.interpreterKinds.SignedEnglish = this.bookingModel.number_of_signed_english_interpreters_required > 0;
    this.interpreterKinds.IndigenousSign = this.bookingModel.number_of_indigenous_sign_interpreters_required > 0;

    this.interpreterKinds.DeafBlind =
      this.interpreterKinds.VisualFrame || this.interpreterKinds.Tactile || this.interpreterKinds.Platform;
    this.interpreterKinds.OtherLanguage =
      this.interpreterKinds.ASL ||
      this.interpreterKinds.BSL ||
      this.interpreterKinds.ISL ||
      this.interpreterKinds.SignedEnglish ||
      this.interpreterKinds.IndigenousSign;
  }

  shouldSelectDeafBlindOtherLanguage() {
    const checkSubType = (_parentType: string) => {
      return Object.keys(InterpreterParentType).every((subType) => {
        const parentType = InterpreterParentType[subType];
        return parentType === _parentType ? !this.interpreterKinds[subType] : true;
      });
    };

    return (
      (this.interpreterKinds.DeafBlind && checkSubType('DeafBlind')) ||
      (this.interpreterKinds.OtherLanguage && checkSubType('OtherLanguage'))
    );
  }

  isInterpreterLessThanTwo() {
    if (this.interpreterKinds.Auslan && this.bookingModel.number_of_auslan_interpreters_required < 2) {
      return true;
    } else if (this.interpreterKinds.Deaf && this.bookingModel.number_of_deaf_interpreters_required < 2) {
      return true;
    } else if (this.interpreterKinds.Captioning && this.bookingModel.number_of_captioners_required < 2) {
      return true;
    } else if (this.interpreterKinds.Notetaking && this.bookingModel.number_of_note_takers_required < 2) {
      return true;
    } else if (
      this.interpreterKinds.VisualFrame &&
      this.bookingModel.number_of_visual_frame_interpreters_required < 2
    ) {
      return true;
    } else if (this.interpreterKinds.Tactile && this.bookingModel.number_of_tactile_interpreters_required < 2) {
      return true;
    } else if (this.interpreterKinds.Platform && this.bookingModel.number_of_platform_interpreters_required < 2) {
      return true;
    } else if (this.interpreterKinds.ASL && this.bookingModel.number_of_asl_interpreters_required < 2) {
      return true;
    } else if (this.interpreterKinds.BSL && this.bookingModel.number_of_bsl_interpreters_required < 2) {
      return true;
    } else if (this.interpreterKinds.ISL && this.bookingModel.number_of_isl_interpreters_required < 2) {
      return true;
    } else if (
      this.interpreterKinds.SignedEnglish &&
      this.bookingModel.number_of_signed_english_interpreters_required < 2
    ) {
      return true;
    } else if (
      this.interpreterKinds.IndigenousSign &&
      this.bookingModel.number_of_indigenous_sign_interpreters_required < 2
    ) {
      return true;
    } else {
      return false;
    }
  }

  createModal(title: string, message: string, options?: ModalOptions) {
    let config: MdDialogConfig = {
      disableClose: true,
    };
    config.viewContainerRef = this.viewContainerRef;
    this.dialogRef = this.dialog.open(PopupComponent, config);
    this.dialogRef.componentInstance.title = title;
    this.dialogRef.componentInstance.cancelTitle = (options && options.cancelTitle) || 'BACK';
    this.dialogRef.componentInstance.okTitle =
      options && options.okTitle ? options.okTitle : this.forEdit ? 'UPDATE' : 'CREATE';
    this.dialogRef.componentInstance.closeVal = (options && options.closeVal) || false;
    this.dialogRef.componentInstance.popupMessage = message;
  }

  isLongBooking() {
    return this.calculateTimeDiff() >= _ONE_HOUR * 12;
  }

  public onBookingForChange() {
    this.bookableModel.reset();
    this.bookingModel.preference_allocations_attributes = [];
    this.bookingModel.client.organisation_billing_account.organisation_billing_address = new Address();
    this.bookingModel.client.organisation_primary_contact = new Contact();
    this.bookingModel.deaf_person = new DEAFContact();
    this.bookingModel.primaryContact = new Contact();
    this.bookingModel.requested_by = new Contact();
    this.bookingModel.bookable_id = 0;
    this.bookingModel.cbBlueCard = false;
    this.bookingModel.cbYellowCard = false;
    this.bookingModel.cbPoliceCheck = false;
    this.bookingModel.cbImminisations = false;
    this.bookingModel.cbForm27A = false;
    this.bookingModel.cbworkWithChildCheck = false;
    this.bookingModel.isBookingBeRecorded = false;
    this.currentUserIsClient = this.bookingModel.bookable_type !== 'OrganisationalRepresentative';
    this.currentUserIsContact = this.bookingModel.bookable_type !== 'OrganisationalRepresentative';
  }

  public onBookingSelectionChange(selectedObject: IndividualClient | OrganisationalRepresentative) {
    this.bookingModel.preference_allocations_attributes = [];
    this.bookingModel.bookable_id = selectedObject.id;
    this.onPrimaryContactSelectionChange();
    this.discardPrimaryContact();
    this.setRequestedBy();
    this.onClientSelectionChange();
    this.setInvoiceField();
    this.onSpecialInstruction();
    this.setUserCertifications(selectedObject);
  }

  isRequestedByExpanded() {
    const {first_name, last_name, email, mobile_number, phone_number} = this.bookingModel.requested_by;
    if (!this.requestedBy && (!first_name || !last_name)) {
      this.expandRequestedBy();
    }
    return this.requestedBy;
  }

  expandRequestedBy() {
    this.requestedBy = true;
  }

  closeRequestedBy(...formModels: NgModel[]) {
    if (!validateNgModels(formModels)) {
      return;
    }
    this.requestedBy = undefined;
  }

  setClientAsRequestedBy() {
    let user = this.isUserAdminORBookOfficer ? this.bookable : GLOBAL.currentUser;
    if (user) {
      const {first_name, last_name, email, mobile: mobile_number, phone_number} = user;
      Object.assign(this.bookingModel.requested_by, {first_name, last_name, email, mobile_number, phone_number});
    }
  }

  setRequestedBy() {
    let user = this.isUserAdminORBookOfficer ? this.bookable : GLOBAL.currentUser;
    if (user) {
      const {first_name, last_name, email, mobile: mobile_number, phone_number} = user;
      Object.assign(this.bookingModel.requested_by, {first_name, last_name, email, mobile_number, phone_number});
    }
    this.requestedBy = undefined;
  }

  public isMoreInterpreterNeeded() {
    /* One hour for VicDeaf and Two Hour for not Vicdeaf , like DSQ*/
    const maxLimit =
      GLOBAL.currentUser && GLOBAL.currentUser.business_name && GLOBAL.currentUser.business_name === 'Vicdeaf'
        ? _ONE_HOUR
        : 2 * _ONE_HOUR;
    return this.calculateTimeDiff() > maxLimit;
  }

  calculateTimeDiff() {
    const startTime = moment(this.bookingStartTime, 'DD hh:mm A').format('DD HH:mm:ss');
    const endTime = moment(this.bookingEndTime, 'DD hh:mm A').format('DD HH:mm:ss');

    const timeDiff = Math.abs(moment.duration(endTime).asMilliseconds() - moment.duration(startTime).asMilliseconds());
    return timeDiff;
  }

  dateRestrictions() {
    let today = new Date();
    let year = today.getFullYear();
    this.minDate = new Date();
    this.isUserAdminORBookOfficer ? this.minDate.setFullYear(year - 5) : this.minDate.setDate(today.getDate());
    this.maxDate = new Date();
    this.maxDate.setFullYear(year + 5);
    this.minDateForRecurrenceEnd = this.getMinDateForRecurringBookingEnd();
  }

  public resetRecurringDays() {
    this.repeat_days = this.repeat_days.map((day) => ({...day, selected: false}));

    if (this.bookingDate) {
      this.repeat_days[this.bookingDate.getDay()].selected = true;
      this.minDateForRecurrenceEnd = this.getMinDateForRecurringBookingEnd();
    }
  }

  getMinDateForRecurringBookingEnd() {
    return this.bookingDate || this.minDate;
  }

  setTime() {
    let startTime = this.bookingModel.utcToBookingTimeZone(this.bookingModel.venue.start_time_iso);
    let endTime = this.bookingModel.utcToBookingTimeZone(this.bookingModel.venue.end_time_iso);
    let currentDate = new Date();

    this.bookingStartTime = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate(),
      moment.duration(startTime).get('hours'),
      moment.duration(startTime).get('minutes')
    );
    this.bookingEndTime = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate(),
      moment.duration(endTime).get('hours'),
      moment.duration(endTime).get('minutes')
    );
    this.updateDuration();
  }

  roundOffMinutes() {
    let currentDate = new Date();
    this.defaultDateTime = currentDate;
    let minute = Math.ceil(currentDate.getMinutes() / 5) * 5;
    this.defaultDateTime.setMinutes(minute);
  }

  utcToBookingTimeZoneDate(time: string) {
    let timeZone = Booking.getNamedTimeZone(
      this.bookingModel.venue.state,
      this.bookingModel.venue.post_code.toString()
    );
    return momentTimeZone(time)
      .tz(timeZone)
      .format('YYYY-MM-DD');
  }

  public timeFormatting() {
    let selectedDate = this.datePipe.transform(this.bookingDate, 'yyyy-MM-dd');
    let startTime = moment(this.bookingStartTime, 'hh:mm A').format('HH:mm:ss');
    let endTime = moment(this.bookingEndTime, 'hh:mm A').format('HH:mm:ss');
    this.bookingModel.venue.start_time_iso = selectedDate + 'T' + startTime + this.bookingModel.getGMTOffsetWithDST(selectedDate);
    this.bookingModel.venue.end_time_iso = selectedDate + 'T' + endTime + this.bookingModel.getGMTOffsetWithDST(selectedDate);
  }

  public setBookingTime(timeFor: string, event) {
    let start_time = event.target.value.replace(/\s/g, '');
    let regularExpression = new RegExp('^([0-9]|0[0-9]|1[0-2]):([0-5][0-9])s*?([AP]M)?$', 'i');
    let res = regularExpression.exec(start_time);
    let bookingTime;
    if (res) {
      let hh = res[1];
      let mm = res[2];
      let amPm = res[3];
      let hour;
      if (+mm % 5 !== 0) {
        let temp_mm = +mm % 5;
        mm = (+mm + (5 - temp_mm)).toString();
        if (+mm === 60) {
          mm = '00';
          hh = (+hh + 1).toString();
        }
      }
      if (+hh < 10) {
        hour = '0' + hh;
      }
      if (amPm) {
        amPm = amPm.toLocaleUpperCase();
        if (amPm === 'PM') {
          hour = +hh + 12;
        } else {
          hour = hh;
        }
      } else {
        hour = hh;
      }
      bookingTime = hour + ':' + mm + ' 00';
      if (isNaN(Date.parse(bookingTime))) {
        return;
      }
      const currentDate = new Date(Date.now());
      bookingTime = new Date(bookingTime);
      if (timeFor === 'start') {
        this.bookingStartTime = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          currentDate.getDate(),
          bookingTime.getHours(),
          bookingTime.getMinutes()
        );
      } else {
        this.bookingEndTime = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          currentDate.getDate(),
          bookingTime.getHours(),
          bookingTime.getMinutes()
        );
      }
      this.updateDuration();
    }
  }

  updateDuration() {
    if (!this.bookingStartTime || !this.bookingEndTime) {
      return;
    }

    const duration = moment.duration(+moment(this.bookingEndTime).startOf('minute') - +moment(this.bookingStartTime).startOf('minute'));
    this.bookingDuration = `${duration.hours() < 10 ? 0 : ''}${duration.hours()}:${
      duration.minutes() < 10 ? 0 : ''
    }${duration.minutes()}`;
  }

  public isRecurrenceDayCheckboxDisabled(day) {
    const isDisabled = this.bookingDate && this.bookingDate.getDay() === this.repeat_days.indexOf(day);
    if (isDisabled) {
      this.repeat_days[this.repeat_days.indexOf(day)].selected = true;
    }
    return isDisabled;
  }

  getRecurrenceDays() {
    return this.repeat_days.filter((day) => day.selected).map((day) => day.value);
  }

  attachAddressAutocomplete() {
    this.mapsAPILoader.load().then(() => {
      const autocomplete = new google.maps.places.Autocomplete(this.searchAddressElementRef.nativeElement, {
        types: ['address'],
        componentRestrictions: {country: 'au'},
      });
      autocomplete.addListener('place_changed', () => {
        this.ngZone.run(() => {
          const place: google.maps.places.PlaceResult = autocomplete.getPlace();
          if (place.geometry !== undefined || place.geometry !== null) {
            this.bookingModel.venue.unit_number = '';
            this.bookingModel.venue.street_number = '';
            this.setMapVenueAddress(place);
            if (!this.isVenueAddressValid()) {
              this.expandVenueAddress(true);
            }
            this.calculateVenueDistance();
          }
        });
      });
    });
  }

  // TODO: can be improve
  setMapVenueAddress(place) {
    if (place.types[0] === 'subpremise') {
      for (let component of place.address_components) {
        let addressType = MapAddressTypes[component['types'][0]];
        let subpremiseAddressAttr = MapSubpremiseAddressAttrs[component['types'][0]];
        if (subpremiseAddressAttr != null) {
          this.bookingModel.venue[subpremiseAddressAttr] = component[addressType];
        }
      }
    } else {
      let firstCompont = place.address_components[0];
      if (firstCompont['types'][0] === 'street_number' && firstCompont.short_name.indexOf('/') === 1) {
        this.bookingModel.venue.unit_number = firstCompont.short_name.split('/')[0];
        this.bookingModel.venue.street_number = firstCompont.short_name.split('/')[1];
        for (let component of place.address_components) {
          if (component['types'][0] !== 'street_number') {
            let addressType = MapAddressTypes[component['types'][0]];
            let addressAttr = MapAddressAttrs[component['types'][0]];
            if (addressAttr != null) {
              this.bookingModel.venue[addressAttr] = component[addressType];
            }
          }
        }
      } else {
        for (let component of place.address_components) {
          let addressType = MapAddressTypes[component['types'][0]];
          let addressAttr = MapAddressAttrs[component['types'][0]];
          if (addressAttr != null) {
            this.bookingModel.venue[addressAttr] = component[addressType];
          }
        }
      }
    }
  }

  calculateVenueDistance() {
    if (this.isVenueAddressValid()) {
      const {unit_number, street_number, street_name, suburb, state, post_code} = this.bookingModel.venue;
      const originAddress = [unit_number, street_number, street_name, suburb, state, post_code, 'Australia'];
      const dedicatedGpo = GLOBAL.VICDEAF_STATES.includes(state) ? GLOBAL.GPO_ADDRESS_ONE : GLOBAL.GPO_ADDRESS_TWO;

      this.ngZone.runOutsideAngular(() => {
        this.gmapApi.getMinDistance([originAddress.join(', ')], [dedicatedGpo]).then((value) => {
          this.ngZone.run(() => {
            let travelCost = Number((value / 1000).toFixed(2)) > 40;
            if (this.userModel) {
              this.userModel.interpreter_type = travelCost ? 'Rural' : 'Metro';
            }
            this.travelCostApplicable = travelCost;
          });
        });
      });
    } else {
      this.travelCostApplicable = false;
    }
  }

  public isVenueAddressExpand() {
    return this.venueAddressExpand;
  }

  public expandVenueAddress(value: boolean) {
    this.venueAddressExpand = value;
  }

  isVenueAddressValid() {
    const {street_number, street_name, suburb, state, post_code} = this.bookingModel.venue;
    return street_number && street_name && suburb && state && post_code;
  }

  public setStandardVenueAddress() {
    const user = this.isUserAdminORBookOfficer ? this.bookable : GLOBAL.currentUser;
    if (!user) {
      return;
    }
    const {unit_number, street_number, street_name, suburb, state, post_code} =
      this.isUserOrgRep() || user.type === 'OrganisationalRepresentative'
        ? user.organisation_attributes.address_attributes
        : this.isIndClient() || user.type === 'IndividualClient'
        ? user.address_attributes
        : ({} as any);

    Object.assign(this.bookingModel.venue, {unit_number, street_number, street_name, suburb, state, post_code});
    this.expandVenueAddress(true);
  }

  onPrimaryContactChange() {
    let user = this.isUserAdminORBookOfficer ? this.bookable : GLOBAL.currentUser;
    if (user) {
      ['first_name', 'last_name', 'email', 'mobile_number'].forEach((field) => {
        let currentUserFieldMap = {mobile_number: 'mobile'};
        let currentUserField = currentUserFieldMap[field] || field;
        let value = this.currentUserIsContact ? user[currentUserField] : '';
        this.bookingModel.primaryContact[field] = value;
      });
    }
  }

  isPrimaryContactExpanded() {
    const {first_name, last_name, email, mobile_number} = this.bookingModel.primaryContact;
    if (!this.primaryContact && (!first_name || !last_name || !email || !mobile_number)) {
      this.changePrimaryContact();
    }
    return this.primaryContact;
  }

  changePrimaryContact() {
    this.primaryContact = true;
  }

  closePrimaryContact(...formModels: NgModel[]) {
    if (!validateNgModels(formModels)) {
      return;
    }
    this.primaryContact = undefined;
  }

  discardPrimaryContact() {
    this.primaryContact = undefined;
  }

  onPrimaryContactSelectionChange() {
    let user = this.isUserAdminORBookOfficer ? this.bookable : GLOBAL.currentUser;
    if (!user) {
      return;
    }
    const {first_name, last_name, email} = this.currentUserIsContact
      ? user
      : {first_name: '', last_name: '', email: ''};
    Object.assign(this.bookingModel.primaryContact, {first_name, last_name, email});
  }

  public natureOfAppointmentChange() {
    let val: BOOKING_NATURE = <BOOKING_NATURE>BOOKING_NATURE[this.bookingModel.raw_nature_of_appointment];
    this.specificAppointmentTypes = BA.DISSCUSSION_ITEM[BOOKING_NATURE[val]];

    const specificAppointmentTypesValues =  this.specificAppointmentTypes.map(value => value
      .replace(' (Please specify in Notes)', '')
      .replace(' (Please specify length in Notes)', '')
    );

    if (!specificAppointmentTypesValues.includes(this.bookingModel.specific_nature_of_appointment)) {
      this.bookingModel.specific_nature_of_appointment = '';
    }
    if (this.bookingModel.raw_nature_of_appointment === 'Other') {
      this.bookingModel.specific_nature_of_appointment = 'Please specify in special instructions for interpreter(s)';
    }
  }

  setIsStandardInstruction() {
    if (this.userModel) {
      this.isStandardInstruction = this.bookingModel.special_instructions === this.userModel.special_instructions;
    }
  }

  public onSpecialInstruction() {
    const user = this.isUserAdminORBookOfficer ? this.bookable : GLOBAL.currentUser;
    if (!user) {
      return;
    }
    const {special_instructions} = this.isStandardInstruction ? user : {special_instructions: ''};
    this.bookingModel.special_instructions = special_instructions;
  }

  public handleBulkUploadSelect(evt) {
    let files = evt.target.files;
    let file = files[0];
    evt.srcElement.value = '';
    // File uploader wont add a duplicate file
    if (files && file) {
      let reader = new FileReader();
      reader.onload = this._handleBulkUploadReaderLoaded.bind(this);
      reader.readAsDataURL(file);
    }
  }

  _handleBulkUploadReaderLoaded(readerEvt) {
    this.spinnerService.requestInProcess(true);
    this.bookingService.bulkUploadBookings(readerEvt.target.result).subscribe(
      (res: any) => {
        if (res.status === 204) {
          this.notificationServiceBus.launchNotification(
            false,
            'The Bookings in your bulk upload file have been created.'
          );
          let route = this.rolePermission.getDefaultRouteForCurrentUser();
          this.router.navigate([route]);
        }
        this.spinnerService.requestInProcess(false);
      },
      (errors) => {
        this.spinnerService.requestInProcess(false);
        let e = errors.json() || '';
        this.notificationServiceBus.launchNotification(true, e);
      }
    );
  }

  public handleFileSelect(evt) {
    let files = evt.target.files;
    let file = files[0];
    evt.srcElement.value = '';

    // File uploader wont add a duplicate file
    if (file) {
      let reader = new FileReader();
      reader.onload = this._handleReaderLoaded.bind(this, file.name);
      reader.readAsDataURL(file);
    }
  }

  _handleReaderLoaded(fileName, readerEvt) {
    this.bookingModel.documents_attributes.push({document: readerEvt.target.result, document_file_name: fileName});
  }

  public removeDocument(item) {
    if (this.bookingModel.documents_attributes.filter((d) => d.document_file_name === item.file.name).length > 0) {
      item.remove();
      this.bookingModel.documents_attributes = this.bookingModel.documents_attributes.filter(
        (d) => d.document_file_name !== item.file.name
      );
    }
  }

  public removeOldDocument(docID) {
    let obj = {id: docID, _destroy: '1'};
    this.deleteDocuments = [...this.deleteDocuments, obj];
    this.oldDocuments = this.oldDocuments.filter((doc) => doc.id !== docID);
  }

  public onClientSelectionChange() {
    let user = this.isUserAdminORBookOfficer ? this.bookable : GLOBAL.currentUser;
    if (!user) {
      return;
    }
    const {
      first_name,
      last_name,
      email,
      mobile: mobile_number,
      ndis_id,
      eaf_id: eaf,
      ur_id: ur_number,
      general_notes,
      claim_notes,
    } = this.currentUserIsClient
      ? user
      : {
          first_name: '',
          last_name: '',
          email: '',
          mobile: '',
          ndis_id: '',
          eaf_id: '',
          ur_id: '',
          general_notes: '',
          claim_notes: '',
        };
    Object.assign(this.bookingModel.deaf_person, {
      first_name,
      last_name,
      email,
      mobile_number,
      ndis_id,
      eaf,
      ur_number,
    });
    Object.assign(this.bookingModel, {general_notes, claim_notes});
  }

  checkClientAvailability(...formModels: NgModel[]) {
    if (!validateNgModels(formModels)) {
      return;
    }
    this.spinnerService.requestInProcess(true);
    this.filterUsers('first_name', this.bookingModel.deaf_person.first_name);
    this.filterUsers('last_name', this.bookingModel.deaf_person.last_name);
    this.filterUsers('mobile', this.bookingModel.deaf_person.mobile_number);
    this.filterUsers('email', this.bookingModel.deaf_person.email);
    this.filterUsers('eaf_id', this.bookingModel.deaf_person.eaf);
    this.filterUsers('ndis_id', this.bookingModel.deaf_person.ndis_id);
    this.filterUsers('ur_id', this.bookingModel.deaf_person.ur_number);
    this.bookingService.checkClientAvailability(GLOBAL._filterUserVal)
      .subscribe((res: any) => {
        this.spinnerService.requestInProcess(false);
        this.isClientAvailable = true;
        if (res.status === 200) {
          this.isClientFound = true;
          this.hasClientId = true;
          this.clientId = res.data.id;
          if (this.clientId === this.previousClientId) {
              this.availabilityMsg = this.bookingModel.deaf_person.first_name + ' ' + this.bookingModel.deaf_person.last_name +
              ' is connected to this booking';
            this.clientAvailability = true;
          } else {
              this.availabilityMsg = this.bookingModel.deaf_person.first_name + ' ' + this.bookingModel.deaf_person.last_name + ' exists in our system';
          }
        }
      },
        errors => {
        this.spinnerService.requestInProcess(false);
        this.isClientAvailable = false;
        if (errors.status === 404) {
          this.isClientFound = false;
            this.availabilityMsg = this.bookingModel.deaf_person.first_name + ' ' + this.bookingModel.deaf_person.last_name + ' does not exist in our system';
        } else {
          let e = errors.json();
          this.notificationServiceBus.launchNotification(true, e);
        }
        });
  }

  filterUsers(field: string, value: string) {
    if (value && value.length) {
      this.userFilter[field] = value;
    } else {
      delete this.userFilter[field];
      this.filterUserParams.delete('user[' + field + ']');
    }
    for (let k in this.userFilter) {
      if (this.userFilter.hasOwnProperty(k)) {
        this.filterUserParams.set('user[' + k + ']', this.userFilter[k]);
      }
    }
    GLOBAL._filterUserVal = this.filterUserParams;
  }

  disableSearch() {
    return ((this.bookingModel.deaf_person.email === '' && this.bookingModel.deaf_person.mobile_number === '') ||
      this.bookingModel.deaf_person.first_name === '' ||
      this.bookingModel.deaf_person.last_name === '');
  }

  onChangeLinkedClient() {
    this.bookingModel.linkedClientId = this.clientAvailability ? this.clientId : null;
  }

  public isDeafClientExpanded() {
    const {first_name, last_name, email, mobile_number} = this.bookingModel.deaf_person;
    if (!this.deafClientContact && (!first_name || !last_name || !email || !mobile_number)) {
      this.changeDeafClient();
    }
    return this.deafClientContact;
  }

  public changeDeafClient() {
    this.deafClientContact = true;
  }

  public closeDeafClient(...formModels: NgModel[]) {
    if (!validateNgModels(formModels)) {
      return;
    }
    this.deafClientContact = undefined;
  }

  public discardDeafClient() {
    this.deafClientContact = undefined;
  }

  public isDeafClientExtraExpanded() {
    // const {ndis_id, eaf, ur_number} = this.bookingModel.deaf_person;
    // if (!this.deafClientContactExtra && (!ndis_id || !eaf || !ur_number)) {
    //   this.editDeafClientExtra();
    // }
    return this.deafClientContactExtra;
  }

  public changeDeafClientExtra() {
    this.deafClientContactExtra = true;
  }

  public closeDeafClientExtra() {
    this.deafClientContactExtra = undefined;
  }

  public discardDeafClientExtra() {
    this.deafClientContactExtra = undefined;
  }

  public onPreferredInterpreterChange() {
    if (!this.showPreferred) {
      this.showProfilePreferred = false;
      const removePreferred = (item) => {
        if (item.preference !== 'preferred') {
          return item;
        }
        return {...item, _destroy: 1};
      };
      this.oldInterpreterPreference = this.bookingModel.preference_allocations_attributes = this.bookingModel.preference_allocations_attributes.map(
        removePreferred
      );
    } else if (!this.forEdit) {
      if (this.isUserAdminORBookOfficer && this.bookable !== undefined) {
        this.hasPreferred = this.bookable.prefferedInterpreters.some((item) => item.preference === 'preferred');
      } else if (this.userModel.type === 'OrganisationalRepresentative' || this.userModel.type === 'IndividualClient') {
        this.hasPreferred = this.userModel.prefferedInterpreters.some((item) => item.preference === 'preferred');
      }
    }
  }

  public onProfilePreferredChange() {
    if (!this.forEdit) {
      let prefInt = this.userModel.prefferedInterpreters.filter((item) => item.preference === 'preferred');
      if (this.showProfilePreferred) {
        this.oldInterpreterPreference = this.oldInterpreterPreference.concat(prefInt);
      } else {
        this.oldInterpreterPreference = this.oldInterpreterPreference.filter((item) =>
          prefInt.every((item2) => item2.interpreter_id !== item.interpreter_id)
        );
      }
      this.filterUserPreference(this.oldInterpreterPreference);
    }
  }

  public onBlockedChange() {
    if (!this.showBlocked) {
      this.showProfileBlocked = false;
      const removeBlocked = (item) => {
        if (item.preference !== 'blocked') {
          return item;
        }
        return {...item, _destroy: 1};
      };
      this.oldInterpreterPreference = this.bookingModel.preference_allocations_attributes = this.bookingModel.preference_allocations_attributes.map(
        removeBlocked
      );
    } else if (!this.forEdit) {
      if (this.isUserAdminORBookOfficer && this.bookable !== undefined) {
        this.hasBlocked = this.bookable.prefferedInterpreters.some((item) => item.preference === 'blocked');
      } else if (this.userModel.type === 'OrganisationalRepresentative' || this.userModel.type === 'IndividualClient') {
        this.hasBlocked = this.userModel.prefferedInterpreters.some((item) => item.preference === 'blocked');
      }
    }
  }

  public onProfileBlockedChange() {
    if (!this.forEdit) {
      let blockInt = this.userModel.prefferedInterpreters.filter((item) => item.preference === 'blocked');
      if (this.showProfileBlocked) {
        this.oldInterpreterPreference = this.oldInterpreterPreference.concat(blockInt);
      } else {
        this.oldInterpreterPreference = this.oldInterpreterPreference.filter((item) =>
          blockInt.every((item2) => item2.interpreter_id !== item.interpreter_id)
        );
      }
      this.filterUserPreference(this.oldInterpreterPreference);
    }
  }

  filterUserPreference(interpreters) {
    if (this.forEdit) {
      this.bookingModel.preference_allocations_attributes = interpreters;
    } else {
      this.bookingModel.preference_allocations_attributes = [];
      interpreters.forEach((item) => {
        if (this.showPreferred) {
          if (item.preference === 'preferred' && !item.hasOwnProperty('_destroy')) {
            this.bookingModel.preference_allocations_attributes.push({
              interpreter_id: item.interpreter_id,
              preference: item.preference,
            });
          } else if (item.hasOwnProperty('_destroy')) {
            this.userModel.prefferedInterpreters = this.userModel.prefferedInterpreters.filter(
              (item2) => item2.interpreter_id !== item.interpreter_id
            );
          }
        }

        if (this.showBlocked) {
          if (item.preference === 'blocked' && !item.hasOwnProperty('_destroy')) {
            this.bookingModel.preference_allocations_attributes.push({
              interpreter_id: item.interpreter_id,
              preference: item.preference,
            });
          } else if (item.hasOwnProperty('_destroy')) {
            this.userModel.prefferedInterpreters = this.userModel.prefferedInterpreters.filter(
              (item2) => item2.interpreter_id !== item.interpreter_id
            );
          }
        }
      });
    }
  }

  setUserCertifications(userModel: IndividualClient | OrganisationalRepresentative) {
    if (!userModel) {
      return;
    }
    const {
      blueCard,
      yellowCard,
      immunisations,
      policeCheck,
      workWithChildCheck,
      form27A,
      isRecordedBooking,
    } = userModel;
    Object.assign(this.bookingModel, {
      cbBlueCard: blueCard,
      cbYellowCard: yellowCard,
      cbImminisations: immunisations,
      cbPoliceCheck: policeCheck,
      cbworkWithChildCheck: workWithChildCheck,
      cbForm27A: form27A,
      isBookingBeRecorded: isRecordedBooking,
    });
  }

  onStandardInvoice() {
    if (GLOBAL.currentUser instanceof OrganisationalRepresentative) {
      let currentUser = <OrganisationalRepresentative>GLOBAL.currentUser;

      this.bookingModel.client.organisation_primary_contact = this.standardInvoice
        ? currentUser.organisation_primary_contact
        : new Contact();
      this.bookingModel.client.organisation_billing_account.external_reference = this.standardInvoice
        ? currentUser.organisation_billing_account.external_reference
        : '';
      this.bookingModel.client.organisation_billing_account.organisation_billing_address = this.standardInvoice
        ? currentUser.organisation_billing_account.organisation_billing_address
        : new Address();
    } else if (this.isUserAdminORBookOfficer) {
      this.setInvoiceField();
    } else {
      let currentUser = <IndividualClient>GLOBAL.currentUser;

      this.bookingModel.client.organisation_primary_contact = this.standardInvoice
        ? currentUser.individual_client_primary_contact
        : new Contact();
      this.bookingModel.client.organisation_billing_account.external_reference = this.standardInvoice
        ? currentUser.individual_client_billing_account.external_reference
        : '';
      this.bookingModel.client.organisation_billing_account.organisation_billing_address = this.standardInvoice
        ? currentUser.individual_client_billing_account.organisation_billing_address
        : new Address();
    }
  }

  isStandardInvoice() {
    let isStandard = false;
    let user = this.bookable;
    if (user) {
      if (user instanceof IndividualClient) {
        isStandard =
          this.bookingModel.client.organisation_primary_contact === user.individual_client_primary_contact &&
          this.bookingModel.client.organisation_billing_account.organisation_billing_address ===
            user.individual_client_billing_account.organisation_billing_address &&
          this.bookingModel.client.organisation_billing_account.external_reference ===
            user.individual_client_billing_account.external_reference;
      } else {
        isStandard =
          this.bookingModel.client.organisation_primary_contact === user.organisation_primary_contact &&
          this.bookingModel.client.organisation_billing_account.organisation_billing_address ===
            user.organisation_billing_account.organisation_billing_address &&
          this.bookingModel.client.organisation_billing_account.external_reference ===
            user.organisation_billing_account.external_reference;
      }
    }
    return isStandard;
  }

  setInvoiceField() {
    let user = this.bookable;
    if (!user) {
      return;
    }
    if (user instanceof IndividualClient) {
      this.bookingModel.client.organisation_primary_contact =
        this.standardInvoice && user.individual_client_primary_contact
          ? user.individual_client_primary_contact
          : new Contact();
      this.bookingModel.client.organisation_billing_account.organisation_billing_address = this.standardInvoice
        ? user.individual_client_billing_account.organisation_billing_address
        : new Address();
      this.bookingModel.client.organisation_billing_account.external_reference = this.standardInvoice
        ? user.individual_client_billing_account.external_reference
        : '';
      this.bookingModel.deaf_person.eaf = this.standardInvoice ? user.eaf_id : '';
      this.bookingModel.deaf_person.ur_number = this.standardInvoice ? user.ur_id : '';
      this.bookingModel.deaf_person.ndis_id = this.standardInvoice ? user.ndis_id : '';
    } else {
      this.bookingModel.client.organisation_primary_contact =
        this.standardInvoice && user.organisation_primary_contact ? user.organisation_primary_contact : new Contact();
      this.bookingModel.client.organisation_billing_account.organisation_billing_address =
        this.standardInvoice && user.organisation_billing_account
          ? user.organisation_billing_account.organisation_billing_address
          : new Address();
      this.bookingModel.client.organisation_billing_account.external_reference =
        this.standardInvoice && user.organisation_billing_account
          ? user.organisation_billing_account.external_reference
          : '';
      this.bookingModel.deaf_person.eaf = '';
      this.bookingModel.deaf_person.ndis_id = '';
      this.bookingModel.deaf_person.ur_number = '';
    }
    this.discardBillingContact();
    this.discardBillingDetail();
  }

  public isBillingContactExpanded() {
    const {first_name, last_name, email, phone_number} = this.bookingModel.client.organisation_primary_contact;
    if (!this.billingContact && (!first_name || !last_name || !email || !phone_number)) {
      this.changeBillingContact();
    }
    return this.billingContact;
  }

  public changeBillingContact() {
    this.billingContact = true;
  }

  public closeBillingContact(...formModels: NgModel[]) {
    if (!validateNgModels(formModels)) {
      return;
    }
    this.billingContact = undefined;
  }

  public discardBillingContact() {
    this.billingContact = undefined;
  }

  public isBillingDetailExpanded() {
    const {
      street_number,
      street_name,
      suburb,
      post_code,
      state,
    } = this.bookingModel.client.organisation_billing_account.organisation_billing_address;
    if (!this.billingDetail && (!street_number || !street_name || !suburb || !post_code || !state)) {
      this.changeBillingDetail();
    }
    return this.billingDetail;
  }

  public changeBillingDetail() {
    this.billingDetail = true;
  }

  public closeBillingDetail(...formModels: NgModel[]) {
    if (!validateNgModels(formModels)) {
      return;
    }
    this.billingDetail = undefined;
  }

  public discardBillingDetail() {
    this.billingDetail = undefined;
  }

  public get TermAndConditionUrl() {
    return getTermsConditionUrl();
  }
}

function camelCaseToUnderScore(bookable_type: string): string {
  return bookable_type.replace(/([a-zA-Z])(?=[A-Z])/g, '$1_').toLowerCase();
}

function getOrgName(item): string {
  if (!item) {
    return '';
  }
  let organisationName = '';
  if (item instanceof OrganisationalRepresentative) {
    organisationName = item.organisation_name.toUpperCase() + ' - ';
  }
  return organisationName + item.first_name + ' ' + item.last_name;
}

function validateNgModels(formModels: NgModel[]) {
  if (!formModels.some((formModel) => formModel.invalid)) {
    return true;
  }
  formModels.forEach((formModel) => {
    return formModel.control.markAsTouched({onlySelf: true});
  });
  return false;
}

function validateAllFormFields(formGroup: FormGroup) {
  Object.keys(formGroup.controls).forEach((field) => {
    const control = formGroup.get(field);
    if (control instanceof FormControl) {
      control.markAsTouched({onlySelf: true});
    } else if (control instanceof FormGroup) {
      validateAllFormFields(control);
    }
  });
}



// WEBPACK FOOTER //
// ./src/app/booking-management/booking-form/booking-form.component.ts